import { ProductLineId } from '@insurancemenu/imenu-components-lib/dist/types/ProductLineId';
import { CartBrowsingCriteria, RateSegment } from '@insurancemenu/imenu-components-lib/dist/types/productCardsTypings';
import { UserType } from './general';

export interface EnrollmentPeriod {
  id: string;
  proposals: Proposal[];
  groupId: string;
  hasConfirmedProposal: boolean;
  effectiveDate: string;
  productLineIds: ProductLineId[];
  stage: EnrollmentStage;
  type: EnrollmentType;
  checklist?: Checklist | null;
  postSubmitChecklist?: Checklist | null;
  rateSegment: RateSegment;
  installedProposal?: InstalledProposal | null;
  sponsorId: string;
  parentGroupId?: string;
}

export type DashboardEnrollmentPresenter = Pick<
  EnrollmentPeriod,
  'id' | 'groupId' | 'effectiveDate' | 'productLineIds' | 'stage' | 'proposals'
>;

export interface Proposal {
  cartId: string;
  cartVersion?: number;
  name: string;
  createdOn?: string;
  createdBy?: CreatedBy;
  productLines: ProductLine[];
  confirmed: boolean;
  rateSegment: RateSegment;
  preferred?: boolean;
  initiateFormAnswers?: Record<string, string>;
  // BE does not differentiate checklist types.
  checklist?: RFQChecklist | QuoteChecklist;
  documents?: RfqDocument[];
  status?: RfqProposalStatus;
  quotingStatus?: RfqProposalStatus;
}

export interface SaveProposalInput {
  groupId: string;
  cartName: string;
  cartId: string;
  effectiveDate: string;
  productLines: ProductLine[];
  createdBy?: InputPerformedBy;
  initiateFormAnswers?: Record<string, string>;
}

export interface InstalledProposal {
  productLines: InstalledProductLine[];
}

export interface ProductLine {
  id: ProductLineId;
  productType: ProductType;
  confirmedPortfolioId: string | null;
  bcIndex?: number;
  browsingCriteria?: CartBrowsingCriteria;
  carrierIds?: string[];
}
export interface InstalledProductLine {
  type: EnrollmentType;
  id: ProductLineId;
  terminationEffectiveDate: string | null;
  carrierPortfolioId: string;
  imPortfolioId: string;
  carrierPortfolioName: string;
  products: InstalledProduct[];
  exhibitUrls: string[];
  additionalSyncData: Record<string, string>;
}

export interface InstalledProduct {
  imProductId: string;
  carrierProductId: string;
  carrierProductName: string;
  rates: InstalledRate[];
  renewalCarrierProductId?: string;
  renewalCarrierProductName?: string;
}

export interface InstalledRate {
  rateStructure: string;
  currentRate: string;
  renewalRate: string;
}

export interface CreatedBy {
  id: string;
  name: string;
}

export enum EnrollmentType {
  NEW_SALE = 'NEW_SALE',
  RESUBMIT_NEW_SALE = 'RESUBMIT_NEW_SALE',
  RENEWAL = 'RENEWAL',
  INSTALLED = 'INSTALLED',
  TERMINATED = 'TERMINATED',
}

export enum ProductType {
  NEW_SALE = 'NEW_SALE',
  CHANGE_PLAN = 'CHANGE_PLAN',
  CANCEL = 'CANCEL',
  RENEW = 'RENEW',
  DONT_PROVIDE = 'DONT_PROVIDE',
  ADD_BENEFIT = 'ADD_BENEFIT',
}

export enum ChecklistCompletionStatus {
  COMPLETED = 'COMPLETED',
  READY_TO_BEGIN = 'READY_TO_BEGIN',
  CANNOT_COMPLETE_YET = 'CANNOT_COMPLETE_YET',
  IN_PROGRESS = 'IN_PROGRESS',
}

export enum QuoteChecklistCard {
  INITIATE_SHELF = 'INITIATE_SHELF',
  INITIATE_SHELF_ADD_EMPLOYEES = 'INITIATE_SHELF_ADD_EMPLOYEES',
  INITIATE_SHELF_ADD_QUOTE = 'INITIATE_SHELF_ADD_QUOTE',
  /**
   * Temporary step used until the Add Quote page is implemented.
   */
  INITIATE_SHELF_BROWSING = 'INITIATE_SHELF_BROWSING',
  ZYWAVE_QUOTE_REDIRECT = 'ZYWAVE_QUOTE_REDIRECT',
}

export enum ChecklistCard {
  PROPOSAL_V2 = 'PROPOSAL_V2',
  COMPANY_INFO = 'COMPANY_INFO',
  EMPLOYER_CONTRIBUTION = 'EMPLOYER_CONTRIBUTION',
  ADD_EMPLOYEES = 'ADD_EMPLOYEES',
  CENSUS_IMPORT = 'CENSUS_IMPORT',
  EMPLOYEE_ELECTIONS = 'EMPLOYEE_ELECTIONS',
  FORMS = 'FORMS',
  RENEWAL_FORMS = 'RENEWAL_FORMS',
  REVIEW_SUBMIT = 'REVIEW_SUBMIT',
  REQUIRED_DOCUMENTS = 'REQUIRED_DOCUMENTS',
  DOWNLOAD_CONFIRMED_PROPOSAL = 'DOWNLOAD_CONFIRMED_PROPOSAL',
  BUILD = 'BUILD',
  ENROLLMENT_PACKET = 'ENROLLMENT_PACKET',
  SUMMARY_PAGE = 'SUMMARY_PAGE',
  PRODUCT_OUTPUTS = 'PRODUCT_OUTPUTS',
  POST_SUBMIT_DOCUMENTS = 'POST_SUBMIT_DOCUMENTS',
  CLOSE_DEAL = 'CLOSE_DEAL',
  MEMBER_ID_CARDS = 'MEMBER_ID_CARDS',
  WELCOME_KIT = 'WELCOME_KIT',
  DEFAULT_BROKER = 'DEFAULT_BROKER',
  ALLIED_BROKER = 'ALLIED_BROKER',
  SUNLIFE_BROKER = 'SUNLIFE_BROKER',
}

export enum ChecklistButtonStateText {
  VIEW_EDIT = 'VIEW_EDIT',
  VIEW = 'VIEW',
  UNAVAILABLE = 'UNAVAILABLE',
  CONTINUE = 'CONTINUE',
  REVIEW_SUBMIT = 'REVIEW_SUBMIT',
  PROCESSING = 'PROCESSING',
}

export enum RFQChecklistCard {
  // Initiate RFQ checklist
  INITIATE_RFQ = 'INITIATE_RFQ',
  INITIATE_SHELF = 'INITIATE_SHELF',
  INITIATE_LEVEL_FUNDED = 'INITIATE_LEVEL_FUNDED',
  INITIATE_ADD_EMPLOYEES = 'INITIATE_ADD_EMPLOYEES',
  INITIATE_RFQ_DOCUMENTS = 'INITIATE_RFQ_DOCUMENTS',
  SUBMIT_INITIATE_RFQ = 'SUBMIT_INITIATE_RFQ',
  PICK_CARRIERS = 'PICK_CARRIERS',
  INITIATE_SHELF_BROWSING = 'INITIATE_SHELF_BROWSING',
  INITIATE_LEVEL_BROWSING = 'INITIATE_LEVEL_BROWSING',
  INITIATE_BROWSING_CARRIERS = 'INITIATE_BROWSING_CARRIERS',

  // RFQ response checklist
  BROKER_MESSAGE = 'BROKER_MESSAGE',
  RFQ_RESPONSE_DOCUMENTS = 'RFQ_RESPONSE_DOCUMENTS',
  RFQ_DETAILS = 'RFQ_DETAILS',
  BUILD_RFQ = 'BUILD_RFQ',
  CREATE_PORTFOLIO = 'CREATE_PORTFOLIO',
  RATE_RFQ = 'RATE_RFQ',
  ADD_COLLATERAL = 'ADD_COLLATERAL',
  RELEASE = 'RELEASE',
  PROPOSAL_RESPONSE = 'PROPOSAL_RESPONSE',

  // Level Funded response checklist
  MONITOR_ADD_EMPLOYEES = 'MONITOR_ADD_EMPLOYEES',
  DOWNLOAD_IMQS = 'DOWNLOAD_IMQS',
  RATE_LEVEL_FUNDED = 'RATE_LEVEL_FUNDED',

  // OBSOLETE: Use a specific page type based on the checklist.
  UPLOAD_DOCUMENTS = 'UPLOAD_DOCUMENTS',
  INITIATE_BROWSING = 'INITIATE_BROWSING',
}

export interface ChecklistOption<T> {
  card: T;
  buttonState: ChecklistButtonStateText;
  completionStatus: ChecklistCompletionStatus;
}

export interface Checklist {
  checklistOptions: ChecklistOption<ChecklistCard>[];
}
export interface RFQChecklist {
  checklistOptions: ChecklistOption<RFQChecklistCard>[];
}

export interface QuoteChecklist {
  checklistOptions: ChecklistOption<QuoteChecklistCard>[];
}

export interface InputProposalV2 {
  cartName: string;
  cartId: string;
  productLines: ProductLine[];
  createdBy?: InputPerformedBy;
}

export interface InputPerformedBy {
  id: string;
  name: string;
}

export enum RfqProposalStatus {
  INITIATED = 'INITIATED',
  PENDING_UW = 'PENDING_UW',
  RATED = 'RATED',
  PENDING_RELEASE = 'PENDING_RELEASE',
  RELEASED = 'RELEASED',
  RESPONSE_PENDING = 'RESPONSE_PENDING',
  RESPONSE_READY = 'RESPONSE_READY',
  ACTION_REQUIRED = 'ACTION_REQUIRED',
  EMPLOYEES_ADDED = 'EMPLOYEES_ADDED',
}

export enum ProposalAction {
  CONFIRM = 'CONFIRM',
  UPDATE = 'UPDATE',
  SWITCH = 'SWITCH',
}

export enum USER_ACTIONS {
  // Allied
  COMPLETE_BROKER = 'COMPLETE_BROKER',

  COMPLETE_PROPOSAL = 'COMPLETE_PROPOSAL',
  COMPLETE_COMPANY_INFO = 'COMPLETE_COMPANY_INFO',
  COMPLETE_CONTRIBUTION = 'COMPLETE_CONTRIBUTION',
  COMPLETE_CENSUS_IMPORT = 'COMPLETE_CENSUS_IMPORT',
  COMPLETE_ELECTIONS = 'COMPLETE_ELECTIONS',
  UNCOMPLETE_ELECTIONS = 'UNCOMPLETE_ELECTIONS',
  COMPLETE_REQUIRED_DOCUMENTS = 'COMPLETE_REQUIRED_DOCUMENTS',
  UNCOMPLETE_REQUIRED_DOCUMENTS = 'UNCOMPLETE_REQUIRED_DOCUMENTS',
  UNCOMPLETE_ADD_EMPLOYEES = 'UNCOMPLETE_ADD_EMPLOYEES',
  BYPASS_CENSUS = 'BYPASS_CENSUS',
  COMPLETE_FORMS = 'COMPLETE_FORMS',
  COMPLETE_REVIEW_SUBMIT = 'COMPLETE_REVIEW_SUBMIT',
  COMPLETE_WEB_FORMS = 'COMPLETE_WEB_FORMS',
  COMPLETE_RENEWAL_FORMS = 'COMPLETE_RENEWAL_FORMS',
  WIPE_ELECTION = 'WIPE_ELECTION',

  // POST-SUBMIT
  COMPLETE_POST_SUBMIT_DOCUMENTS = 'COMPLETE_POST_SUBMIT_DOCUMENTS',
  UNCOMPLETE_POST_SUBMIT_DOCUMENTS = 'UNCOMPLETE_POST_SUBMIT_DOCUMENTS',

  // Initiate RFQ
  COMPLETE_INITIATE_RFQ = 'COMPLETE_INITIATE_RFQ',
  COMPLETE_INITIATE_RFQ_DOCUMENTS = 'COMPLETE_INITIATE_RFQ_DOCUMENTS',
  COMPLETE_ADD_EMPLOYEES = 'COMPLETE_ADD_EMPLOYEES',
  COMPLETE_INITIATE_BROWSING = 'COMPLETE_INITIATE_BROWSING',
  COMPLETE_ADD_QUOTE = 'COMPLETE_ADD_QUOTE',

  // Response RFQ
  COMPLETE_RFQ_DETAILS = 'COMPLETE_RFQ_DETAILS',
  COMPLETE_BUILD_RFQ = 'COMPLETE_BUILD_RFQ',
  COMPLETE_CREATE_PORTFOLIO = 'COMPLETE_CREATE_PORTFOLIO',
  COMPLETE_RATE_RFQ = 'COMPLETE_RATE_RFQ',
  COMPLETE_ADD_COLLATERAL = 'COMPLETE_ADD_COLLATERAL',
  COMPLETE_RELEASE = 'COMPLETE_RELEASE',
  COMPLETE_PROPOSAL_RESPONSE = 'COMPLETE_PROPOSAL_RESPONSE',
  COMPLETE_DOWNLOAD_IMQS = 'COMPLETE_DOWNLOAD_IMQS',
}

export enum EnrollmentStage {
  RENEWAL = 'RENEWAL',
  INSTALLED = 'INSTALLED',
  QUOTING = 'QUOTING',
  ENROLLING = 'ENROLLING',
  NEW_LEAD = 'NEW_LEAD',
  INVITED = 'INVITED',
  ACTIVE = 'ACTIVE',
  SUBMITTED = 'SUBMITTED',
  ARCHIVED = 'ARCHIVED',
  SOLD = 'SOLD',
}

export interface EmailReceiver {
  email: string;
  userType: UserType;
}

export type EnrollmentCardType = 'common' | 'archived' | 'ee';

export type InstalledEnrollmentType = Pick<
  EnrollmentPeriod,
  'effectiveDate' | 'productLineIds' | 'stage' | 'id' | 'installedProposal' | 'groupId'
>;

export interface RfqDocument {
  id: string;
  name: string;
  type: string;
  path: string;
  uploadDateTime: string;
}

export type FinalBrowsingCriteria = {
  [key in string]: CartBrowsingCriteria | null;
};

export type ChecklistType = 'DEFAULT' | 'POST_SUBMIT';

export type EnrollmentCoreData = Pick<
  EnrollmentPeriod,
  'id' | 'groupId' | 'effectiveDate' | 'productLineIds' | 'stage' | 'type' | 'rateSegment' | 'sponsorId'
>;

export type EnrollmentProposalData = {
  proposals: Proposal[];
  confirmedProposal: Proposal | null;
};

export interface InputRFQCarrierByPLine {
  productLineId: ProductLineId;
  carrierId: string;
  sponsorId: string;
  primaryRecipient: string;
  ccRecipients: string[];
  portfolios: { id: string; name: string }[];
}

export type RfqProposalChecklistData = {
  groupId: string;
  parentGroupId?: string;
  sponsorId: string;
  checklist?: RFQChecklist;
};
